// Build a concise WhatsApp alert message for a supervisor when a care program
// reaches "red" priority. Keeps the message short so it fits a single WhatsApp
// preview bubble.
//
// The supervisor phone number is stored per-device in localStorage so each
// nurse configures it once on her own phone.

import { computePriority } from "@/lib/priority";
import { detectDialysisCriticalReasons } from "@/lib/dialysis-alerts";
import { detectGastrostomyCriticalReasons } from "@/lib/gastrostomy-alerts";

const PHONE_KEY = "ce_supervisor_phone";

// Strip everything except digits and a single leading "+".
export function normalizePhone(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const plus = s.startsWith("+") ? "+" : "";
  const digits = s.replace(/\D+/g, "");
  return `${plus}${digits}`;
}

// Phone usable for wa.me (digits only, no "+").
export function waPhone(raw) {
  return normalizePhone(raw).replace(/^\+/, "");
}

// E.164-ish validation: + optional, then 8-15 digits.
export function isValidPhone(raw) {
  const n = normalizePhone(raw);
  return /^\+?\d{8,15}$/.test(n);
}

export function getSupervisorPhone() {
  try {
    return localStorage.getItem(PHONE_KEY) || "";
  } catch {
    return "";
  }
}

export function setSupervisorPhone(value) {
  try {
    const n = normalizePhone(value);
    if (n) localStorage.setItem(PHONE_KEY, n);
    else localStorage.removeItem(PHONE_KEY);
  } catch {
    /* ignore quota / privacy mode */
  }
}

// Returns { isCritical, reasons[] } using the same logic as the dashboard.
export function evaluateCritical(program) {
  const p = computePriority(program);
  const baseReasons = p.reasons || [];
  const dialysisReasons = detectDialysisCriticalReasons(
    program?.closing?.peritoneal_dialysis
  );
  const pegReasons = detectGastrostomyCriticalReasons(
    program?.closing?.gastrostomy
  );
  const reasons = [...baseReasons, ...dialysisReasons, ...pegReasons];
  return {
    isCritical: p.level === "red" || dialysisReasons.length > 0 || pegReasons.length > 0,
    reasons,
  };
}

function nowHM() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

// Plain-text message — WhatsApp does not render Markdown so we keep it simple.
export function buildAlertMessage(program, reasons) {
  const s = program?.shift || {};
  const c = program?.closing || {};
  const lines = [];
  lines.push("ALERTA CRITICA - Ciudad Enfermeria");
  lines.push("");
  lines.push(`Paciente: ${s.patient_name || "-"}`);
  const addr = [s.room, s.bed && `#${s.bed}`].filter(Boolean).join(" ");
  if (addr) lines.push(`Direccion: ${addr}`);
  const nurses = (s.nurses || []).filter(Boolean).join(", ");
  if (nurses) lines.push(`Enfermera(s): ${nurses}`);
  lines.push(`Hora del aviso: ${nowHM()}`);
  lines.push("");
  lines.push("Hallazgos criticos:");
  (reasons || []).forEach((r) => lines.push(`- ${r}`));
  if (c.observations) {
    lines.push("");
    lines.push(`Observaciones: ${c.observations}`);
  }
  lines.push("");
  lines.push("Se requiere atencion inmediata.");
  return lines.join("\n");
}

// Open WhatsApp chat (or share sheet) with the message pre-filled and
// addressed to the supervisor. Returns true if a target was opened.
export function openSupervisorWhatsApp(message, phone) {
  const num = waPhone(phone);
  const text = encodeURIComponent(message);
  const url = num
    ? `https://wa.me/${num}?text=${text}`
    : `https://wa.me/?text=${text}`;
  const w = window.open(url, "_blank", "noopener,noreferrer");
  return Boolean(w);
}
